import { endOfDay, startOfDay, startOfMonth, subDays } from "date-fns";

export type PeriodPreset = "today" | "7d" | "30d" | "month";

/**
 * Exatamente os mesmos quatro atalhos, valores e chaves de tradução que
 * AppointmentsPeriodHero / RevenuePeriodHero / StockPeriodHero já usam — não
 * um conjunto próprio do explorador. O gestor não deve encontrar um
 * vocabulário de período diferente só porque mudou de aba.
 */
export const PERIOD_PRESETS: { labelKey: string; value: PeriodPreset }[] = [
  { labelKey: "quickToday", value: "today" },
  { labelKey: "quick7d", value: "7d" },
  { labelKey: "quick30d", value: "30d" },
  { labelKey: "quickThisMonth", value: "month" },
];

const PRESET_VALUES = PERIOD_PRESETS.map((preset) => preset.value);

export function isPeriodPreset(value: unknown): value is PeriodPreset {
  return typeof value === "string" && PRESET_VALUES.includes(value as PeriodPreset);
}

/** Mesma lógica de "applyQuick" das heroes de referência: `to` é sempre o
 *  fim do dia de hoje, `from` recua a partir daí. */
export function resolvePeriodRange(
  preset: PeriodPreset,
  reference: Date = new Date(),
): { from: string; to: string } {
  const to = endOfDay(reference);

  switch (preset) {
    case "today":
      return { from: startOfDay(reference).toISOString(), to: to.toISOString() };
    case "7d":
      return { from: startOfDay(subDays(reference, 6)).toISOString(), to: to.toISOString() };
    case "30d":
      return { from: startOfDay(subDays(reference, 29)).toISOString(), to: to.toISOString() };
    case "month":
    default:
      return { from: startOfMonth(reference).toISOString(), to: to.toISOString() };
  }
}

const isoDay = (value: string) => value.slice(0, 10);

/**
 * A pílula de um preset acende comparando as datas reais do recorte contra o
 * que aquele preset produziria agora — não um campo guardado à parte. É o
 * mesmo padrão de "isQuickActive" das outras heroes de período (Appointments/
 * Revenue/StockPeriodHero): um intervalo escolhido no calendário nunca acende
 * pílula nenhuma, mesmo que numericamente comece no mesmo dia de um preset.
 */
export function isPeriodPresetActive(
  from: string,
  to: string,
  preset: PeriodPreset,
  reference: Date = new Date(),
): boolean {
  const candidate = resolvePeriodRange(preset, reference);

  if (preset === "today") {
    return isoDay(from) === isoDay(candidate.from) && isoDay(to) === isoDay(candidate.to);
  }

  return isoDay(from) === isoDay(candidate.from);
}

/** "1 – 31 de ago. de 2026" quando cabe, senão as duas datas por extenso. */
export function formatPeriodRange(from: string, to: string, locale: string): string {
  if (!from || !to) return "—";

  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";

  const sameMonth =
    start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();

  if (sameMonth) {
    const monthYear = new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" }).format(end);
    return `${start.getDate()} – ${end.getDate()} ${monthYear}`;
  }

  const short = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" });
  return `${short.format(start)} – ${short.format(end)}`;
}
